import { Fragment, useCallback, useEffect, useState } from 'react'
import { InterestRateDto } from '@/api/Interest-Rates/InterestRateDto'
import { PageData } from '@/api/Pagination/Types'
import { Pager, usePager } from '@/components/components/Pager'
import { FilterType } from '@/components/Filter'
import { SelectFiltersFacade } from '@/components/Filter/ClientFields/SelectFiltersFacade'
import { getInterestRatesFields } from '@/components/Filter/InterestRatesFields/getInterestRatesFields'
import {
  createGridState,
  useGridFilter,
  useGridState,
  useGridStatusState,
} from '@/features/DataGrid'
import { calculateSkipIndex } from '@/features/DataGrid/helpers'
import { isGoBack } from '@/lib/react-router-dom'
import { buildQuery, buildSortQuery } from '@/utils/QueryHelpers'
import { TableWithExport } from '@/utils/table/withExport'
import { TableStateContext, TableStateContextData } from '@/utils/TableStateContext'
import { SortDescriptor } from '@progress/kendo-data-query'
import { GridColumn as Column, Grid, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { UpdateClientStatus } from 'api/Clients/Types/UpdateClientStatus'
import { Spinner } from 'components/Spinner'
import { CompositeFilterDescriptor, GridFilterActionState } from 'store/grid'
import { useApiClient } from 'utils/api/ApiClient'
import { WalletsApiClient } from 'utils/api/walletsApiClient'

import { ClientNavigation, ClientTab } from '../Navigation/ClientNavigation'
import clientDetailsInterestRatesColumns from './clientDetailsInterestRatesColumns'

interface Props {
  data: ClientDetailedDto
  forceRefresh(): void
  updateClientStatus(values: UpdateClientStatus): void
}

const ClientDetailsInterestRates: React.FC<Props> = ({ data, updateClientStatus }) => {
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: false })

  const walletsApiClient = useApiClient(WalletsApiClient)

  const [interestRates, setInterestRates] = useState<InterestRateDto[]>([])

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const { sessionFilterPageSize } = usePager({ namespace: 'client-details-interest-rates' })

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (gridFilterState: GridFilterActionState): Promise<PageData<InterestRateDto[]>> => {
          const searchQuery = gridFilterState.filter ? getQuery(gridFilterState.filter) : null
          const sortQuery = gridFilterState.sort ? getSort(gridFilterState.sort) : null
          const pageIndex = gridFilterState.pageIndex || 1
          const pageSize = sessionFilterPageSize || gridFilterState.pageSize || 50
          const take = sessionFilterPageSize || gridFilterState.take || 50

          const response = await walletsApiClient.getInterestRates(data.id, {
            search: searchQuery,
            sort: sortQuery,
            pageIndex,
            pageSize,
          })

          if (response?.items) {
            gridActions.setGridFilters({
              take: pageSize,
              skip: calculateSkipIndex(pageIndex, take),
              pageIndex,
            })
            setInterestRates(response.items)
          }
          setTableData({ ...tableData, meta: response })
          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<InterestRateDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const initialGridState = createGridState(
      { skip: 0, pageSize: sessionFilterPageSize || 50, take: sessionFilterPageSize || 50 },
      gridState,
      {
        isGoBack,
      }
    )
    const gridFilterState = selectFilterForGoBackHistory(initialGridState)
    const response = await callback({ ...gridFilterState, pageSize: initialGridState.pageSize })
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(sortDescriptors ?? gridState.sort)
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (params?: { query?: string | null }) => {
      const searchQuery = params?.query !== undefined ? params.query : getQuery()
      const sortQuery = getSort(gridState.sort)
      const pageIndex = gridState.pageIndex ?? 1
      const pageSize = gridState.pageSize ?? 50

      const pageQuery = {
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
      }

      const response = await walletsApiClient.getInterestRates(data.id, pageQuery)
      const total = response.totalResults

      if (response?.items) {
        setInterestRates(response.items)
      }
      gridActions.setGridFilters({
        take: pageSize,
        skip: (pageIndex - 1) * pageSize,
        total,
        pageIndex,
      })
    },
    [data.id, getQuery, getSort, gridActions, gridState] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const clientId = data.id

    const response = await walletsApiClient.getInterestRates(clientId, {
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
    })

    if (response?.items) {
      setInterestRates(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)
    const clientId = data.id

    const response = await walletsApiClient.getInterestRates(clientId, {
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
    })

    if (response?.items) {
      setInterestRates(response.items)
    }

    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })

    setTableData({ ...tableData, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page
    const pageSize = gridState.take
    const clientId = data.id

    const response = await walletsApiClient.getInterestRates(clientId, {
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize,
    })

    if (response?.items) {
      setInterestRates(response.items)
    }

    gridActions.setGridFilters({
      ...gridState,
      pageIndex,
      total: response.totalResults,
    })

    setTableData({ ...tableData, meta: response })
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <Fragment>
      <ClientNavigation
        isEdit={false}
        data={data}
        tab={ClientTab.INTEREST_RATES}
        updateClientStatus={updateClientStatus}
      />
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.InterestRateOverview}
        filterFields={[...getInterestRatesFields()]}
        getQuery={getQuery}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport data={interestRates}>
          <Grid
            {...gridState}
            resizable
            data={interestRates}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            pageable
            pager={() => (
              <Pager
                namespace='client-details-interest-rates'
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take}
                skip={gridState.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
          >
            {clientDetailsInterestRatesColumns
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column key={idx + 2} {...column} />
              ))}
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </Fragment>
  )
}
export default ClientDetailsInterestRates
